'use client';

import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ChevronDown } from '@/components/icons';

import { LANGUAGES_TITLE } from '@/helpers/languages';

import './index.css';

/**
 * @param {object} props
 * @param {import('@/helpers/languages').StoryLanguage[]} props.languages
 */
export const LanguageSelector = ({ languages }) => {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  const current = languages.find((language) => language.current);

  const hovered = useRef(false);

  const toggle = useCallback(() => {
    setOpen((state) => hovered.current || !state);
  }, []);

  const handlePointerEnter = useCallback((event) => {
    if (event.pointerType === 'mouse') {
      hovered.current = true;
      setOpen(true);
    }
  }, []);

  const handlePointerLeave = useCallback((event) => {
    if (event.pointerType === 'mouse') {
      hovered.current = false;
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event) => {
      if (!ref.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  if (!current) {
    return null;
  }

  return (
    <div
      ref={ref}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      className={classNames('language-selector', {
        ['language-selector--open']: open,
      })}
    >
      <button
        type="button"
        className="language-selector__button"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={LANGUAGES_TITLE[current.locale.shortCode]}
      >
        {current.locale.shortName}
        <ChevronDown className="language-selector__button__icon" />
      </button>

      <div className="language-selector__panel">
        <p className="language-selector__title">
          {LANGUAGES_TITLE[current.locale.shortCode]}
        </p>

        <ul className="language-selector__list">
          {languages.map((language) => (
            <li key={language.locale.shortCode}>
              <a
                href={language.href}
                hrefLang={language.locale.shortCode}
                lang={language.locale.shortCode}
                aria-current={language.current ? 'page' : undefined}
                className={classNames('language-selector__link', {
                  ['language-selector__link--current']: language.current,
                })}
              >
                {language.locale.title}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
